/**
 * Compare-sales accent palette + the small primitives that read from it
 * (panel tint, signed-delta chip). Lives in components/inspector/shared
 * so any future side-by-side compare view (Skimmer / Pooltrackr) can
 * reach for the same vocabulary instead of redeclaring its own colours.
 *
 * Kept out of theme/colors.ts because nothing else in the app uses
 * these tones - they're scoped to the compare-sales surface only.
 */
import React from 'react'
import { Box } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { formatSignedDelta } from '../../../utils/salesFormatters'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'

/**
 * Shared font + numeric-alignment SX for numeric values across the
 * Compare Sales surface (line-item amounts, settlement summary,
 * Amount due hero, delta chips, item counts). Now an alias of the
 * app-wide canonical token in ``theme/numericFont.ts`` - the exceptions
 * tables (``tableFormatters.ts``) and per-outlet cells consume the same
 * definition, so Compare and the report tables read identically.
 */
export const COMPARE_NUM_FONT_SX = NUMERIC_FONT_SX

export const COMPARE_COLORS = {
  mismatchBg: 'rgba(236, 28, 90, 0.08)',
  mismatchBorder: '#EC1C5A',
  warningText: '#ed6c02',
  /** Pale-blue tint for the Payments & credits header bar. Scoped to
   *  this surface; no other section of the dialog uses it. Softened
   *  vs MUI's blue[50] so the header reads as a quiet section break,
   *  not a competing emphasis with the totals card above. */
  paymentsHeaderBg: '#F0F6FA',
}

/**
 * Single signed-delta chip used by totals, payments, and Xero line items.
 * Soft red for negative (Xero < LS), soft green for positive (Xero > LS).
 * Returns null for sub-rounding diffs so callers can branch cleanly.
 */
export const renderDeltaChip = (diff: number, isCurrency: boolean): React.ReactNode => {
  const threshold = isCurrency ? 0.005 : 0.05
  if (Math.abs(diff) < threshold) return null
  const positive = diff > 0
  const palette = positive
    ? { fg: '#2E7D32', border: '#A5D6A7', bg: 'rgba(76, 175, 80, 0.10)' }
    : { fg: '#C62828', border: '#EF9A9A', bg: 'rgba(244, 67, 54, 0.10)' }
  const Icon = positive ? ArrowUpwardIcon : ArrowDownwardIcon
  // Strip the sign char from formatSignedDelta - the arrow conveys direction.
  const text = formatSignedDelta(diff, isCurrency).replace(/^[+-]/, '')
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        ...COMPARE_NUM_FONT_SX,
        fontSize: '0.7rem',
        fontWeight: 600,
        lineHeight: 1.2,
        px: 0.6,
        py: '1px',
        borderRadius: 1,
        color: palette.fg,
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <Icon sx={{ fontSize: 12 }} />
      {text}
    </Box>
  )
}
